import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, ArrowUpRight, WifiOff, RotateCw, MailX } from "lucide-react";
import { tokenizeQuery, clientMatchesTokens } from "@/lib/search";

const HEALTH_LABEL = {
    warning: "Delivery warning",
    undeliverable: "Email undeliverable",
};

export default function Clients() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [query, setQuery] = useState("");
    const [deliveryProblemsOnly, setDeliveryProblemsOnly] = useState(false);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const { data } = await api.get("/clients");
            setClients(data);
        } catch (err) {
            setLoadError(err);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const deliveryProblemsCount = useMemo(
        () => clients.filter(c => {
            const s = c?.email_health?.status;
            return s === "warning" || s === "undeliverable";
        }).length,
        [clients]
    );

    // Tokenize once per query change so the filter is O(rows × tokens),
    // not O(rows × tokens × keystrokes-worth-of-string-work). MUST live
    // above the early-return so React sees the same hook order every render.
    const queryTokens = useMemo(() => tokenizeQuery(query), [query]);

    if (loadError && clients.length === 0) {
        return (
            <AppShell breadcrumbs={["Clients"]} onClientCreated={load}>
                <div className="max-w-7xl">
                    <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50/70 p-6 flex items-start gap-4"
                        data-testid="clients-load-error">
                        <div className="mt-0.5 shrink-0 w-10 h-10 rounded-full bg-white border border-amber-200 flex items-center justify-center">
                            <WifiOff className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="font-display text-lg text-slate-900">We couldn't load your clients right now</h2>
                            <p className="text-sm text-slate-600 mt-1 max-w-lg">
                                The connection blipped for a moment. Your data is safe — nothing was changed.
                            </p>
                            <button onClick={load} data-testid="clients-load-error-retry"
                                className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-white border border-slate-200 hover:border-slate-300 text-slate-700 transition">
                                <RotateCw className="w-3.5 h-3.5" /> Retry
                            </button>
                        </div>
                    </div>
                </div>
            </AppShell>
        );
    }

    const filtered = clients.filter((c) => {
        if (!clientMatchesTokens(c, queryTokens)) return false;
        if (deliveryProblemsOnly) {
            const s = c?.email_health?.status;
            return s === "warning" || s === "undeliverable";
        }
        return true;
    });

    return (
        <AppShell breadcrumbs={["Clients"]} onClientCreated={load}>
            <div className="max-w-7xl">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <p className="overline mb-2">Clients</p>
                        <h1 className="font-display text-4xl tracking-tight text-slate-900">All clients</h1>
                        <p className="text-slate-500 mt-2">Every portal you've spun up, in one place.</p>
                    </div>
                    <div className="relative w-full sm:w-72">
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input value={query} onChange={(e) => setQuery(e.target.value)}
                            data-testid="clients-search"
                            className="pl-9 ring-terra"
                            placeholder="Search by company, contact, project or email" />
                    </div>
                </div>

                {/* Filter chip row */}
                <div className="flex items-center gap-2 mb-4">
                    <button
                        type="button"
                        onClick={() => setDeliveryProblemsOnly(v => !v)}
                        data-testid="clients-filter-delivery-problems"
                        aria-pressed={deliveryProblemsOnly}
                        className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            deliveryProblemsOnly
                                ? "bg-rose-50 border-rose-200 text-rose-700"
                                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}>
                        <MailX className="w-3.5 h-3.5" /> Delivery problems
                        {deliveryProblemsCount > 0 && (
                            <span data-testid="clients-filter-delivery-count"
                                className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                    deliveryProblemsOnly
                                        ? "bg-rose-100 text-rose-800"
                                        : "bg-slate-100 text-slate-600"
                                }`}>
                                {deliveryProblemsCount}
                            </span>
                        )}
                    </button>
                    {deliveryProblemsOnly && (
                        <button onClick={() => setDeliveryProblemsOnly(false)}
                            data-testid="clients-filter-delivery-clear"
                            className="text-xs text-slate-500 hover:text-slate-900">Clear</button>
                    )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-200 bg-slate-50/50">
                                <th className="px-6 py-3 font-medium">Company</th>
                                <th className="px-6 py-3 font-medium">Project</th>
                                <th className="px-6 py-3 font-medium">Contact</th>
                                <th className="px-6 py-3 font-medium">Progress</th>
                                <th className="px-6 py-3 font-medium">Status</th>
                                <th className="px-6 py-3 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                [1,2,3,4,5].map((i) => (
                                    <tr key={i} className="border-b border-slate-100">
                                        <td className="px-6 py-4"><div className="skeleton h-4 w-32" /></td>
                                        <td className="px-6 py-4"><div className="skeleton h-4 w-40" /></td>
                                        <td className="px-6 py-4"><div className="skeleton h-4 w-36" /></td>
                                        <td className="px-6 py-4"><div className="skeleton h-2 w-24" /></td>
                                        <td className="px-6 py-4"><div className="skeleton h-4 w-16" /></td>
                                        <td></td>
                                    </tr>
                                ))
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={6}
                                    data-testid="clients-empty-state"
                                    className="px-6 py-16 text-center text-slate-500">
                                    {deliveryProblemsOnly
                                        ? "No clients with delivery problems — every recipient is healthy."
                                        : "No clients match your search."}
                                </td></tr>
                            ) : filtered.map((c) => {
                                const healthStatus = c?.email_health?.status || "healthy";
                                const isUndeliverable = healthStatus === "undeliverable";
                                const isWarning = healthStatus === "warning";
                                return (
                                    <tr key={c.client_id}
                                        onClick={() => navigate(`/clients/${c.client_id}`)}
                                        data-testid={`clients-row-${c.client_id}`}
                                        className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 cursor-pointer transition-colors">
                                        <td className="px-6 py-4 font-medium text-slate-900">
                                            <div className="flex items-center gap-2">
                                                {(isUndeliverable || isWarning) && (
                                                    <span
                                                        data-testid={`clients-row-health-dot-${c.client_id}`}
                                                        title={HEALTH_LABEL[healthStatus]}
                                                        aria-label={HEALTH_LABEL[healthStatus]}
                                                        className={`w-2 h-2 rounded-full shrink-0 ${
                                                            isUndeliverable ? "bg-rose-500" : "bg-amber-500"
                                                        }`} />
                                                )}
                                                <span>{c.company_name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-700">{c.project_name}</td>
                                        <td className="px-6 py-4 text-slate-600">
                                            <div>{c.contact_name}</div>
                                            <div className={`text-xs ${isUndeliverable ? "text-rose-600" : "text-slate-400"}`}>
                                                {c.contact_email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="w-28 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                                <div className="h-full bg-[#C55A46]" style={{ width: `${c.portal?.progress?.percent || 0}%` }} />
                                            </div>
                                            <div className="text-[10px] text-slate-400 mt-1">{c.portal?.progress?.percent || 0}%</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {isUndeliverable ? (
                                                <span data-testid={`clients-row-health-pill-${c.client_id}`}
                                                    className="text-xs px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Email undeliverable</span>
                                            ) : isWarning ? (
                                                <span data-testid={`clients-row-health-pill-${c.client_id}`}
                                                    className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Delivery warning</span>
                                            ) : c.needs_attention ? (
                                                <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Needs attention</span>
                                            ) : (
                                                <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">On track</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-slate-400"><ArrowUpRight className="w-4 h-4" /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </AppShell>
    );
}
